import { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { fetchBusinessSubscription } from 'src/redux/actions/AuthAction';
import { setShowSubscriptionPlanDialog } from 'src/redux/slices/AuthSlice';
import { toast } from 'react-hot-toast';

/**
 * A reusable hook to verify business subscription limits.
 */
export const useSubscriptionCheck = () => {
  const dispatch = useDispatch();
  const businessSubscriptions = useSelector((state) => state.auth.businessSubscriptions || {});
  const loadingSubscriptions = useSelector((state) => state.auth.loadingSubscriptions);

  /**
   * Check if a business has reached the limit for a specific feature.
   * Fetches data transparently if it's not already in Redux.
   */
  const checkLimit = useCallback(
    async ({
      accountType,
      featureKey,
      currentUsageKey,
      customCurrentUsage,
      limitDetails,
      onCancel,
      onLimitReached
    }) => {
      // Personal accounts don't need subscription check
      if (!accountType || !accountType.startsWith("business_")) {
        return true; 
      }

      const parts = accountType.split("_");
      const businessId = parts[1];

      try {
        let subscriptionInfo = businessSubscriptions[businessId];
        
        // If not in Redux, fetch it via Thunk
        if (!subscriptionInfo) {
          const actionResult = await dispatch(fetchBusinessSubscription(businessId)).unwrap();
          subscriptionInfo = actionResult.data;
        }

        if (!subscriptionInfo) {
          toast.error("Failed to load subscription information");
          if (onLimitReached) onLimitReached();
          return false;
        }

        // Handle variations in API response keys safely, or use explicit frontend override
        const currentUsage = customCurrentUsage !== undefined
          ? customCurrentUsage
          : (subscriptionInfo[currentUsageKey] || subscriptionInfo.currentMonthlyEvents || subscriptionInfo.currentTotalEvents || 0);
        const feature = subscriptionInfo.subscriptionPlan?.features?.find(
          (f) => f.featureKey === featureKey
        );

        // Check if feature is enabled and not unlimited
        if (feature && feature.enabled && !feature.isUnlimited) {
          if (currentUsage >= feature.quantity) {
            // Show subscription plan dialog
            dispatch(
              setShowSubscriptionPlanDialog({
                show: true,
                businessId: businessId,
                currentPlan: subscriptionInfo?.subscriptionStatus,
                limitReached: {
                  feature: featureKey,
                  current: currentUsage,
                  limit: feature.quantity,
                  message: limitDetails.dialogMessage(feature.quantity),
                  onCancel: () => {
                    if (onCancel) onCancel();
                  }
                }
              })
            );

            toast.error(limitDetails.toastMessage(feature.quantity));
            
            if (onLimitReached) onLimitReached();
            return false;
          } else {
            // Show success message with remaining count
             if (limitDetails.successMessage) {
               const remaining = feature.quantity - currentUsage;
               toast.success(limitDetails.successMessage(remaining), { duration: 3000 });
             }
             return true;
          }
        }

        return true;
      } catch (error) {
        toast.error("Failed to fetch business subscription information");
        if (onLimitReached) onLimitReached();
        return false;
      }
    },
    [dispatch, businessSubscriptions]
  );

  return { checkLimit, loadingSubscriptions };
};
export default useSubscriptionCheck;
